/**
 * /rate-sheets/$rateSheetId — Rate sheet detail + edit + delete.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button, Chip, IconButton, MenuItem, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import { api } from "@/lib/api";
import { rateSheetDetailQueryOptions, rateSheetKeys } from "@/lib/queries";
import { MoneyField } from "@/components/MoneyField";
import { formatMoney } from "@/lib/money";
import { useToast } from "@/components/Toaster";
import type { RateSheet } from "@/lib/types";

export const Route = createFileRoute("/_auth/rate-sheets/$rateSheetId")({
  component: RateSheetDetailPage,
});

function RateSheetDetailPage() {
  const { rateSheetId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const sheetQ = useQuery(rateSheetDetailQueryOptions(rateSheetId));
  const sheet = sheetQ.data;

  const [editing, setEditing] = useState(false);
  const [laborRows, setLaborRows] = useState<RateSheet["labor"]>([]);
  const [materialRows, setMaterialRows] = useState<RateSheet["materials"]>([]);
  const [equipmentRows, setEquipmentRows] = useState<RateSheet["equipment"]>([]);

  // Enter edit mode — copy current rows
  const startEdit = () => {
    if (!sheet) return;
    setLaborRows(sheet.labor.map((l) => ({ ...l })));
    setMaterialRows(sheet.materials.map((m) => ({ ...m })));
    setEquipmentRows(sheet.equipment.map((e) => ({ ...e })));
    setEditing(true);
  };

  const save = useMutation({
    mutationFn: () =>
      api<RateSheet>(`/api/money/rate-sheets/${rateSheetId}`, {
        method: "PATCH",
        body: {
          labor: laborRows.map(({ id: _i, rate_sheet_id: _r, ...l }) => l),
          materials: materialRows.map(({ id: _i, rate_sheet_id: _r, ...m }) => m),
          equipment: equipmentRows.map(({ id: _i, rate_sheet_id: _r, ...e }) => e),
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rateSheetKeys.detail(rateSheetId) });
      qc.invalidateQueries({ queryKey: rateSheetKeys.all });
      toast.success("Rate sheet updated");
      setEditing(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: () =>
      api(`/api/money/rate-sheets/${rateSheetId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rateSheetKeys.all });
      toast.success("Rate sheet deleted");
      navigate({ to: "/rate-sheets" });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (sheetQ.isLoading) return <div className="p-6 text-slate-500">Loading…</div>;
  if (!sheet) return <div className="p-6 text-slate-500">Rate sheet not found.</div>;

  const rows = editing
    ? { labor: laborRows, materials: materialRows, equipment: equipmentRows }
    : { labor: sheet.labor, materials: sheet.materials, equipment: sheet.equipment };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            size="small"
            onClick={() => navigate({ to: "/rate-sheets" })}
          >
            All Rate Sheets
          </Button>
          <Typography variant="h5">{sheet.name}</Typography>
          <Chip label={sheet.rate_type.replace("_", "-")} size="small" />
        </div>
        <div className="flex gap-2">
          {!editing ? (
            <>
              <Button variant="outlined" size="small" onClick={startEdit}>Edit</Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                disabled={del.isPending}
                onClick={() => { if (confirm("Delete this rate sheet?")) del.mutate(); }}
              >
                Delete
              </Button>
            </>
          ) : (
            <>
              <Button variant="outlined" size="small" onClick={() => setEditing(false)}>Cancel</Button>
              <Button
                variant="contained"
                size="small"
                disabled={save.isPending}
                onClick={() => save.mutate()}
              >
                {save.isPending ? "Saving…" : "Save"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Header info */}
      <div className="bg-white rounded-lg border border-slate-200 p-5 dark:bg-gray-900 dark:border-gray-700">
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <Field label="Effective From" value={sheet.effective_from} />
          <Field label="Effective To" value={sheet.effective_to ?? "Open"} />
          <Field label="Project" value={sheet.project_id ?? "Entity-wide"} />
          <Field label="Description" value={sheet.description} />
        </div>
      </div>

      {/* Labor */}
      <SectionCard
        title="Labor Rates"
        onAdd={editing ? () => setLaborRows((p) => [...p, {
          id: crypto.randomUUID(), rate_sheet_id: rateSheetId,
          craft: "", billing_method: "stipulated",
          regular_rate: "0", ot_multiplier: "1.5", dt_multiplier: "2.0",
          markup_percent: null, notes: null,
        } as any]) : undefined}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Craft</TableCell>
              <TableCell>Method</TableCell>
              <TableCell align="right">Rate / Markup</TableCell>
              <TableCell align="right">OT ×</TableCell>
              <TableCell align="right">DT ×</TableCell>
              {editing && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.labor.map((l, i) => (
              <TableRow key={l.id}>
                <TableCell>
                  {editing ? (
                    <TextField value={l.craft} size="small" variant="standard"
                      onChange={(e) => setLaborRows((rows) => rows.map((r, j) => j === i ? { ...r, craft: e.target.value } : r))} />
                  ) : l.craft}
                </TableCell>
                <TableCell sx={{ textTransform: "capitalize" }}>
                  {l.billing_method.replace("_", " ")}
                </TableCell>
                <TableCell align="right">
                  {l.billing_method === "stipulated"
                    ? `${formatMoney(l.regular_rate)}/hr`
                    : `${l.markup_percent}%`}
                </TableCell>
                <TableCell align="right">{Number(l.ot_multiplier).toFixed(1)}×</TableCell>
                <TableCell align="right">{Number(l.dt_multiplier).toFixed(1)}×</TableCell>
                {editing && (
                  <TableCell>
                    <IconButton size="small" color="error"
                      onClick={() => setLaborRows((rows) => rows.filter((_, j) => j !== i))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionCard>

      {/* Materials */}
      <SectionCard
        title="Material Rules"
        onAdd={editing ? () => setMaterialRows((p) => [...p, {
          id: crypto.randomUUID(), rate_sheet_id: rateSheetId,
          category: null, markup_percent: "15", tax_billable: false,
        } as any]) : undefined}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Category</TableCell>
              <TableCell align="right">Markup %</TableCell>
              <TableCell align="center">Tax Billable</TableCell>
              {editing && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.materials.map((m, i) => (
              <TableRow key={m.id}>
                <TableCell>{m.category ?? "(catch-all)"}</TableCell>
                <TableCell align="right">
                  {editing ? (
                    <TextField value={m.markup_percent} size="small" variant="standard" type="number"
                      onChange={(e) => setMaterialRows((rows) => rows.map((r, j) => j === i ? { ...r, markup_percent: e.target.value } : r))}
                      sx={{ width: 80 }} />
                  ) : `${Number(m.markup_percent).toFixed(1)}%`}
                </TableCell>
                <TableCell align="center">{m.tax_billable ? "Yes" : "No"}</TableCell>
                {editing && (
                  <TableCell>
                    <IconButton size="small" color="error"
                      onClick={() => setMaterialRows((rows) => rows.filter((_, j) => j !== i))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionCard>

      {/* Equipment */}
      <SectionCard
        title="Equipment Rates"
        onAdd={editing ? () => setEquipmentRows((p) => [...p, {
          id: crypto.randomUUID(), rate_sheet_id: rateSheetId,
          equipment_class: "", rate_basis: "daily", rate: "0", notes: null,
        } as any]) : undefined}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Equipment Class</TableCell>
              <TableCell>Rate Basis</TableCell>
              <TableCell align="right">Rate</TableCell>
              {editing && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.equipment.map((e, i) => (
              <TableRow key={e.id}>
                <TableCell>
                  {editing ? (
                    <TextField value={e.equipment_class} size="small" variant="standard"
                      onChange={(ev) => setEquipmentRows((rows) => rows.map((r, j) => j === i ? { ...r, equipment_class: ev.target.value } : r))} />
                  ) : e.equipment_class}
                </TableCell>
                <TableCell sx={{ textTransform: "capitalize" }}>{e.rate_basis}</TableCell>
                <TableCell align="right">
                  {editing ? (
                    <MoneyField value={e.rate} size="small" variant="standard"
                      onChange={(v) => setEquipmentRows((rows) => rows.map((r, j) => j === i ? { ...r, rate: v } : r))}
                      sx={{ width: 90 }} />
                  ) : formatMoney(e.rate)}
                </TableCell>
                {editing && (
                  <TableCell>
                    <IconButton size="small" color="error"
                      onClick={() => setEquipmentRows((rows) => rows.filter((_, j) => j !== i))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionCard>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <span className="text-slate-500">{label}: </span>
      <span className="font-medium">{value ?? "—"}</span>
    </div>
  );
}

function SectionCard({
  title, children, onAdd,
}: {
  title: string;
  children: React.ReactNode;
  onAdd?: () => void;
}) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 dark:bg-gray-900 dark:border-gray-700">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 dark:border-gray-700">
        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
        {onAdd && (
          <Button size="small" startIcon={<AddIcon />} onClick={onAdd}>Add Row</Button>
        )}
      </div>
      <div className="p-2">{children}</div>
    </div>
  );
}
